<template>
    <div>
        <div class="flex gap-2 items-center" :class="{'gap-1': cardsCols >= 5}">
            <redesign-base-icon
                :name="call.direction === 'Incoming' ? redesignCallTypeIcons.incoming : redesignCallTypeIcons.outgoing"
                :color="call.direction === 'Incoming' ? redesignCallTypeIcons.incomingColor: redesignCallTypeIcons.outgoingColor"
                :size="callInfoFontSize"
            />
            <div
                class="relative call-info"
                 :class="{
                    'flex gap-2': cardsCols !== 5,
                    'animated': shouldAnimate
                }"
                :style="callInfoStyling"
            >
                <div
                    v-show="call.callerphone"
                    class="call-info-callerphone"
                >
                    {{ call.callerphone }}
                </div>
                <div
                    v-show="hasCallerName"
                    class="call-info-callername"
                >
                    {{ call.callername }}
                </div>
            </div>
            <redesign-tag
                v-for="(tag, i) in tagsContent"
                :key='i + call.ivrid'
                :color="tag.color"
                :animated="tag.animated"
                :tag-content="tag.tagContent"
                class="gap-2"
                :style="callTagStyling"
            ></redesign-tag>
        </div>
        <div class="hidden text-gray-600 text-main-xs relative">
          <Tooltip placement="top" effect="customized" popper-class="custom" :visible-arrow="false">
              <div slot="content">{{call.ivrid}}</div>
            <div>
                {{'ID'}}
            </div>
          </Tooltip>
<!--            <div v-show="cardsCols === 4" class="animate-even absolute left-0 top-0">-->
<!--                {{ call.callername }}-->
<!--            </div>-->
        </div>
    </div>

</template>

<script setup>
import { computed, watch, ref} from 'vue';
import { useTimer } from "@/composables/useTimer";
import RedesignTag from "@/components/RedesignBaseComponents/RedesignTag.vue";
import RedesignBaseIcon from "@/components/RedesignBaseComponents/RedesignBaseIcon.vue";
import store from "@/store/store";
import {Tooltip} from 'element-ui'
import { redesignCallTypeIcons } from "@/enum/colors";
import get from "lodash/get";
import {realTimeSettings} from "@/enum/defaultWidgetSettings";

const TIMER_ICON = 'Timer'
const { timerDisplayTime, timerState, startTimer } = useTimer(false)
const {
    timerDisplayTime:timerHoldTimeDisplay,
    timerState: timerHoldState,
    startTimer:startHoldTimer,
    stopTimer: stopHoldTimer

} = useTimer(false)

/*Props*/
const props = defineProps({
    call: {
        type: Object,
        default: () => ({})
    },
    settings: {
        type: Object,
        default: () => ({})
    },
    cardsCols: Number
})

/*State*/
const callTagColor = ref('')
let callTagIcon = ref('')
let onHold = ref(false)

/*Computed*/
const thresholdConfig = computed(() => {
    return store.getters['layout/getThresholdConfig']('activeLayout')
})
const callInfoFontSize = computed(() => {
    return get(props.settings, 'callInfoFontSize', realTimeSettings.callInfoFontSize)
})
const callInfoStyling = computed(() => {
    return {
        fontSize: `${callInfoFontSize.value}px`,
        lineHeight: `${callInfoFontSize.value}px`
    }
})
const callTagStyling = computed(() => {
    const callTagFontSize = callInfoFontSize.value - 4

    return {
        fontSize: `${callTagFontSize}px`,
        lineHeight: `${callTagFontSize}px`
    }
})
const ActiveCallLimit = computed(() => thresholdConfig.value.ActiveCallLimit);
const ActiveCallLimitColor = computed(() => thresholdConfig.value.ActiveCallLimitColor);
const ActiveCallWarning = computed(() => thresholdConfig.value.ActiveCallWarning);
const ActiveCallWarningColor = computed(() => thresholdConfig.value.ActiveCallWarningColor);
const HoldTimeLimit = computed(() => thresholdConfig.value.HoldTimeLimit);
const HoldTimeWarning = computed(() => thresholdConfig.value.HoldTimeWarning);
const HoldTimeLimitColor = computed(() => thresholdConfig.value.HoldTimeLimitColor);
const HoldTimeWarningColor = computed(() => thresholdConfig.value.HoldTimeWarningColor);

const callDurationStatuses = computed(() => {
    return {
        limit: {
            sound: 'SoundActiveCallLimit',
            color: thresholdConfig.value.ActiveCallLimitColor,
            property: 'sound active call limit',
            type: 'info',
            notification_value: Number(thresholdConfig.value.ActiveCallLimitColor)
        },
        warning: {
            sound: 'SoundActiveCallWarning',
            color: thresholdConfig.value.ActiveCallWarningColor,
            property: 'sound active call warning',
            type: 'warning',
            notification_value: Number(thresholdConfig.value.ActiveCallWarning)
        }
    }
})

const holdStatuses = computed(() => {
    return {
        limitHold: {
            sound: 'SoundHoldTimeLimit',
            color: thresholdConfig.value.HoldTimeLimitColor,
            property: 'sound hold time limit',
            type: 'hold info',
            notification_value: Number(thresholdConfig.value.HoldTimeLimit)
        },
        warningHold: {
            sound: 'SoundHoldTimeWarning',
            color: thresholdConfig.value.HoldTimeWarningColor,
            property: 'sound hold time warning',
            type: 'hold warning',
            notification_value: Number(thresholdConfig.value.HoldTimeWarning)
        }
    }

})

const holdTagColor = computed(() => {
    return timerHoldState.value >= HoldTimeLimit.value
        ? HoldTimeLimitColor.value
        : timerHoldState.value >= HoldTimeWarning.value
        ? HoldTimeWarningColor.value
        : '';
})

const tagsContent = computed(() => {
    const call = {
        color: callTagColor.value,
        animated: !!callTagColor.value,
        tagContent: [
            {iconName: callTagIcon.value, text: timerDisplayTime.value}
        ]
    }
    const hold = {
        color: holdTagColor.value,
        animated: !!callTagColor.value,
        tagContent: [
            {iconName: 'Pause', text: timerHoldTimeDisplay.value}
        ]
    }
    return onHold.value ? [call, hold] : [call];
})
const hasCallerName = computed(() => {
    return props.call.callerphone !== props.call.callername
})
const shouldAnimate = computed(() => {
    return props.cardsCols >= 5 && hasCallerName.value && props.call.callerphone && props.call.callername
})


/*Methods*/
const callStartedTime = computed(() => {
    return props.call.callStarted_UTC_CLIENT
})
const getDateNow = () => {
    return ref(new Date().getTime());
}
/*Watch*/
watch(() => props.call.callstatus, (val, oldValue) => {
    if (val === 'Hold') {
        startHoldTimer(getDateNow())
        onHold.value = true;

    } else if (oldValue === 'Hold' && val !== oldValue) {
        stopHoldTimer()
        onHold.value = false;
    }
}, { immediate: true });

watch(() => timerState.value, (value) => {
    const newColor = value > Number(ActiveCallLimit.value)
        ? ActiveCallLimitColor.value
        : value > Number(ActiveCallWarning.value)
            ? ActiveCallWarningColor.value
            : '';
    const newIcon = newColor ? TIMER_ICON : '';

    if (newIcon && newIcon !== callTagIcon.value) callTagIcon.value = newIcon;
    if (newColor && newColor !== callTagColor.value) callTagColor.value = newColor;
} )

startTimer(callStartedTime)
</script>


<style lang="scss">
.el-tooltip__popper.is-customized.custom {
  @apply p-4;
  position: relative;
  background: var(--mainBgColor-color);
  color: var(--mainTextColor-color);
  border-width: 0;
  box-shadow: 0 8px 16px -2px rgba(27, 33, 44, 0.12);
  &::after {
    position: absolute;
    content: '\23F7';
    color: var(--mainBgColor-color);
    font-size: 18px;
    left: 50%;
    transform: translateX(-50%);
    bottom: -11px;
  }
}

.call-info {
    &-callerphone {
        @apply font-medium;
    }

    &-callername {
        @apply text-gray-600;
    }

    &.animated {
        .call-info-callerphone {
            animation: fadeAnimationOdd infinite 4s;
        }

        .call-info-callername {
            animation: fadeAnimationEven infinite 4s;

            @apply absolute left-0 top-0;
        }
    }
}

@keyframes fadeAnimationEven {
    0% {
        opacity: 1;
    }
    45% {
        opacity: 1;
    }
    50% {
        opacity: 0;
    }
    95% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

@keyframes fadeAnimationOdd {
    0% {
        opacity: 0;
    }
    45% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    95% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
}
</style>
